import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import supabase from '../supabase/supabase';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck } = FiIcons;

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();

  const isSignupRoute = location.pathname === '/signup';

  const [viewState, setViewState]             = useState(isSignupRoute ? 'signup' : 'login');
  const [email, setEmail]                     = useState('');
  const [password, setPassword]               = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms]     = useState(false);
  const [isPending, setIsPending]             = useState(false);
  const [errorMsg, setErrorMsg]               = useState('');
  const [successMsg, setSuccessMsg]           = useState('');
  const [isCheckingAuth, setIsCheckingAuth]   = useState(true);
  const [isSignedUp, setIsSignedUp]           = useState(false);
  const [isResetRequested, setIsResetRequested] = useState(false);

  // Sync view with URL path changes (unless locked in recovery)
  useEffect(() => {
    const isRecovery = sessionStorage.getItem('st_recovery') === '1';
    if (isRecovery) return;

    if (location.pathname === '/signup') setViewState('signup');
    else if (location.pathname === '/login' && viewState === 'signup') setViewState('login');
  }, [location.pathname, viewState]);

  // Tab title
  useEffect(() => {
    const titles = {
      signup:          'studiotimer / sign up',
      email_verified:  'studiotimer / welcome',
      update_password: 'studiotimer / update password',
    };
    document.title = titles[viewState] || 'studiotimer / login';
  }, [viewState]);

  // Session Check & Flow Routing
  useEffect(() => {
    const isRecovery = sessionStorage.getItem('st_recovery') === '1';
    const isEmailVerified = sessionStorage.getItem('st_email_verified') === '1';

    if (isRecovery) {
      setViewState('update_password');
      setIsCheckingAuth(false);
      return;
    }

    if (isEmailVerified) {
      setViewState('email_verified');
      sessionStorage.removeItem('st_email_verified');
      setIsCheckingAuth(false);
      return;
    }

    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      // Auto-redirect valid sessions, strictly unless they are intentionally on the signup page
      if (session && !isSignupRoute) {
        navigate('/admin-dash', { replace: true });
        return;
      }
      setIsCheckingAuth(false);
    };

    checkSession();
  }, [navigate, isSignupRoute]);

  // Auth Listener to catch background sign-ins
  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        const isRecovery = sessionStorage.getItem('st_recovery') === '1';
        if (!isRecovery && !isSignupRoute && viewState !== 'email_verified') {
          navigate('/admin-dash', { replace: true });
        }
      }
    });

    return () => authListener.subscription?.unsubscribe();
  }, [navigate, isSignupRoute, viewState]);

  // Form Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isPending) return;
    setIsPending(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      if (viewState === 'login') {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate('/admin-dash', { replace: true });

      } else if (viewState === 'signup') {
        if (!acceptedTerms) {
          throw new Error('You must agree to the Terms of Service, Privacy Policy, and Acceptable Use Policy.');
        }
        // Critical: Append emailRedirectTo so validation links don't break on HashRouter
        const { data, error } = await supabase.auth.signUp({ 
          email, 
          password,
          options: {
            emailRedirectTo: window.location.origin + window.location.pathname
          }
        });
        if (error) throw error;
        
        if (data?.session) {
          navigate('/admin-dash', { replace: true });
        } else {
          setIsSignedUp(true);
          setPassword('');
        }

      } else if (viewState === 'forgot_password') {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin + window.location.pathname,
        });
        if (error) throw error;
        setIsResetRequested(true);

      } else if (viewState === 'update_password') {
        if (password !== confirmPassword) throw new Error('Passwords do not match.');
        if (password.length < 6) throw new Error('Password must be at least 6 characters.');

        const { error } = await supabase.auth.updateUser({ password });
        if (error) throw error;

        // Success: Clear flags, sign out to ensure clean state, and redirect to login
        sessionStorage.removeItem('st_recovery');
        await supabase.auth.signOut();
        
        setPassword('');
        setConfirmPassword('');
        setSuccessMsg('Password updated successfully. Please log in.');
        setViewState('login');
        navigate('/login', { replace: true });
      }
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setIsPending(false);
    }
  };

  // View switchers
  const switchToSignup = () => {
    setViewState('signup'); navigate('/signup');
    setErrorMsg(''); setSuccessMsg(''); setAcceptedTerms(false);
    setIsSignedUp(false); setIsResetRequested(false);
    setPassword(''); setConfirmPassword('');
  };

  const switchToLogin = async () => {
    if (viewState === 'update_password') {
      sessionStorage.removeItem('st_recovery');
      await supabase.auth.signOut();
    }
    setViewState('login');
    if (location.pathname !== '/login') navigate('/login');
    setErrorMsg(''); setSuccessMsg('');
    setIsSignedUp(false); setIsResetRequested(false);
    setPassword(''); setConfirmPassword('');
  };

  const switchToForgotPassword = () => {
    setViewState('forgot_password');
    if (location.pathname !== '/login') navigate('/login');
    setErrorMsg(''); setSuccessMsg('');
    setIsSignedUp(false); setIsResetRequested(false);
  };

  const renderSubtitle = () => {
    if (viewState === 'signup' && isSignedUp)                return 'Verification Required';
    if (viewState === 'forgot_password' && isResetRequested) return 'Recovery Sent';
    const map = {
      login:           'Operator Access',
      signup:          'Operator Registration',
      forgot_password: 'Password Recovery',
      update_password: 'Update Password',
      email_verified:  'Account Verified',
    };
    return map[viewState] || 'Operator Access';
  };

  if (isCheckingAuth) {
    return <div className="min-h-screen w-full flex bg-[#000000]" />;
  }

  const showEmailVerified = viewState === 'email_verified';
  const showSignupPending = viewState === 'signup' && isSignedUp;
  const showResetPending  = viewState === 'forgot_password' && isResetRequested;
  const showForm          = !showEmailVerified && !showSignupPending && !showResetPending;

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center bg-[#000000] text-white px-4 selection:bg-white selection:text-black">
      <div className="w-full max-w-[380px] bg-[#111111] border border-[#222222] rounded-2xl p-8 flex flex-col items-center shadow-2xl">

        {/* Header */}
        <div className="mb-10 text-center flex flex-col items-center">
          <h1 className="font-extrabold text-[0.78rem] tracking-[0.28em] text-white uppercase">
            StudioTimer
          </h1>
          <div className="mt-4">
            <span className="text-[10px] font-semibold tracking-[0.15em] text-[#888888] uppercase">
              {renderSubtitle()}
            </span>
          </div>
        </div>

        {/* Status: Email Verified / Welcome */}
        {showEmailVerified && (
          <div className="w-full flex flex-col items-center text-center space-y-5 py-4">
            <div className="w-12 h-12 rounded-full bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center">
              <SafeIcon icon={FiCheck} className="w-5 h-5 text-white" />
            </div>
            <div className="space-y-2">
              <h2 className="text-white font-semibold text-base tracking-wide">You're all set</h2>
              <p className="text-[#888888] text-xs leading-[1.7] tracking-wide">
                Your email has been verified and your account is active. Welcome to StudioTimer.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/admin-dash', { replace: true })}
              className="w-full mt-2 bg-white text-black font-bold text-xs tracking-[0.1em] rounded-xl py-4 hover:bg-[#e0e0e0] transition-all uppercase"
            >
              Enter Dashboard
            </button>
          </div>
        )}

        {/* Status: Signup Pending Verification */}
        {showSignupPending && (
          <div className="w-full flex flex-col items-center text-center space-y-4 py-6">
            <div className="w-12 h-12 rounded-full bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center">
              <SafeIcon icon={FiCheck} className="w-5 h-5 text-white" />
            </div>
            <div className="space-y-2">
              <h2 className="text-white font-semibold text-base tracking-wide">Check your inbox</h2>
              <p className="text-[#888888] text-xs leading-[1.7] tracking-wide">
                We sent a verification link to{' '}
                <span className="text-white font-medium">{email}</span>.
                <br />Click the link to activate your account.
              </p>
            </div>
            <button
              type="button"
              onClick={() => { setIsSignedUp(false); setPassword(''); setErrorMsg(''); }}
              className="mt-4 text-[10px] font-semibold text-[#666666] hover:text-white transition-colors uppercase tracking-[0.15em]"
            >
              ← Back to sign up
            </button>
          </div>
        )}

        {/* Status: Reset Link Sent */}
        {showResetPending && (
          <div className="w-full flex flex-col items-center text-center space-y-4 py-6">
            <h2 className="text-white font-semibold text-base tracking-wide">Check your email</h2>
            <p className="text-[#888888] text-xs leading-[1.7] tracking-wide">
              If an account exists for{' '}
              <span className="text-white font-medium">{email}</span>, a secure reset link will arrive shortly.
            </p>
            <button
              type="button"
              onClick={switchToLogin}
              className="mt-4 text-[10px] font-semibold text-[#666666] hover:text-white transition-colors uppercase tracking-[0.15em]"
            >
              ← Back to login
            </button>
          </div>
        )}

        {/* Active Forms */}
        {showForm && (
          <>
            <form onSubmit={handleSubmit} className="w-full space-y-6">
              {errorMsg && (
                <div className="w-full p-3 rounded-lg bg-[#1a0f0f] border border-[#2a1111] text-[#d95555] text-xs font-medium text-center leading-relaxed">
                  {errorMsg}
                </div>
              )}
              {successMsg && (
                <div className="w-full p-3 rounded-lg bg-[#0f1a0f] border border-[#112a11] text-[#55d955] text-xs font-medium text-center leading-relaxed">
                  {successMsg}
                </div>
              )}

              <div className="space-y-3">
                {(viewState === 'login' || viewState === 'signup' || viewState === 'forgot_password') && (
                  <input
                    type="email"
                    required
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-[#0a0a0a] border border-[#222222] rounded-xl px-4 py-3.5 text-sm font-medium text-white placeholder-[#555555] focus:outline-none focus:border-white transition-colors"
                  />
                )}

                {(viewState === 'login' || viewState === 'signup') && (
                  <input
                    type="password"
                    required
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-[#0a0a0a] border border-[#222222] rounded-xl px-4 py-3.5 text-sm font-medium text-white placeholder-[#555555] focus:outline-none focus:border-white transition-colors"
                  />
                )}

                {viewState === 'update_password' && (
                  <>
                    <input
                      type="password"
                      required
                      placeholder="New Password"
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setErrorMsg(''); }}
                      className="w-full bg-[#0a0a0a] border border-[#222222] rounded-xl px-4 py-3.5 text-sm font-medium text-white placeholder-[#555555] focus:outline-none focus:border-white transition-colors"
                    />
                    <input
                      type="password"
                      required
                      placeholder="Confirm New Password"
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); setErrorMsg(''); }}
                      className={`w-full bg-[#0a0a0a] border rounded-xl px-4 py-3.5 text-sm font-medium text-white placeholder-[#555555] focus:outline-none transition-colors ${
                        confirmPassword && password !== confirmPassword
                          ? 'border-[#d95555] focus:border-[#d95555]'
                          : 'border-[#222222] focus:border-white'
                      }`}
                    />
                    {confirmPassword && password !== confirmPassword && (
                      <p className="text-[10px] text-[#d95555] tracking-wide pl-1">Passwords do not match</p>
                    )}
                  </>
                )}
              </div>

              {viewState === 'signup' && (
                <div className="flex items-start gap-3 mt-2">
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={acceptedTerms}
                    onClick={() => { setAcceptedTerms(!acceptedTerms); setErrorMsg(''); }}
                    className={`mt-0.5 w-[18px] h-[18px] shrink-0 border rounded flex items-center justify-center transition-colors focus:outline-none focus:ring-1 focus:ring-white focus:ring-offset-1 focus:ring-offset-[#111111] ${
                      acceptedTerms ? 'bg-white border-white' : 'bg-[#0a0a0a] border-[#333333]'
                    }`}
                  >
                    {acceptedTerms && <SafeIcon icon={FiCheck} className="w-3.5 h-3.5 text-black" />}
                  </button>
                  <label
                    className="text-[10px] leading-[1.6] text-[#666666] cursor-pointer selection:bg-transparent"
                    onClick={() => { setAcceptedTerms(!acceptedTerms); setErrorMsg(''); }}
                  >
                    I agree to the{' '}
                    <a href="https://gettermscdn.com/view/K4uXe/terms-of-service/en-us" target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-[#aaaaaa] hover:text-white underline decoration-[#333333] hover:decoration-white underline-offset-2 transition-colors">Terms of Service</a>,{' '}
                    <a href="https://gettermscdn.com/view/K4uXe/privacy/en-us" target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-[#aaaaaa] hover:text-white underline decoration-[#333333] hover:decoration-white underline-offset-2 transition-colors">Privacy Policy</a>, and{' '}
                    <a href="https://gettermscdn.com/view/K4uXe/acceptable-use/en-us" target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-[#aaaaaa] hover:text-white underline decoration-[#333333] hover:decoration-white underline-offset-2 transition-colors">Acceptable Use Policy</a>.
                  </label>
                </div>
              )}

              <button
                type="submit"
                disabled={isPending || (viewState === 'update_password' && confirmPassword && password !== confirmPassword)}
                className="w-full bg-white text-black font-bold text-xs tracking-[0.1em] rounded-xl py-4 hover:bg-[#e0e0e0] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#111111] disabled:opacity-40 disabled:cursor-not-allowed transition-all uppercase"
              >
                {isPending ? 'Processing...' : (
                  viewState === 'login'           ? 'Log In'          :
                  viewState === 'signup'          ? 'Create Account'  :
                  viewState === 'forgot_password' ? 'Send Reset Link' :
                                                    'Update Password'
                )}
              </button>
            </form>

            <div className="mt-8 flex flex-col items-center space-y-4 w-full">
              {viewState === 'login' && (
                <>
                  <button type="button" onClick={switchToSignup} className="text-[10px] font-semibold text-[#666666] hover:text-white transition-colors uppercase tracking-[0.15em]">
                    Create an account
                  </button>
                  <button type="button" onClick={switchToForgotPassword} className="text-[10px] font-semibold text-[#666666] hover:text-white transition-colors uppercase tracking-[0.15em]">
                    Forgot Password?
                  </button>
                </>
              )}
              {(viewState === 'signup' || viewState === 'forgot_password' || viewState === 'update_password') && (
                <button type="button" onClick={switchToLogin} className="text-[10px] font-semibold text-[#666666] hover:text-white transition-colors uppercase tracking-[0.15em]">
                  Back to Log In
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}